/**
 * Schemi Zod condivisi per onboarding paziente (PR4).
 *
 * Stesso schema usato lato client (validazione UX react-hook-form) e
 * lato server (validazione di sicurezza Cloud Function). Single source
 * of truth.
 *
 * Convenzioni:
 *  - Date come stringhe ISO 8601 ("YYYY-MM-DD") per input form.
 *  - Stringhe lunghe (textarea) max 2000 char per evitare DoS.
 *  - Tutti i testi in italiano nei messaggi `.error`.
 */

import { z } from 'zod';
import { isValidCodiceFiscale } from '@shared/utils/cf-validator';
import { isValidPhoneIT } from '@shared/utils/phone-validator';
import {
  STATO_CIVILE_VALORI,
  STILE_ALIMENTARE_VALORI,
} from '@shared/constants/scelte-anagrafica';
import { documentoAccettatoSchema } from './consensi';

const trimmedString = (max: number) => z.string().trim().max(max);

/**
 * DEBT-2026-09-26a. Fino a 7 giorni dopo il deploy del portale
 * (`ONBOARDING_TOKEN_TTL_MS`) il wizard vecchio può ancora mandare testo
 * libero. L'enum è il contratto nuovo; la stringa è il ramo transitorio.
 * Non svuota e non rifiuta l'ultimo step.
 */
const statoCivileOnboarding = z.union([
  z.enum(STATO_CIVILE_VALORI),
  trimmedString(50),
]);
const tipoDietaOnboarding = z.union([
  z.enum(STILE_ALIMENTARE_VALORI),
  trimmedString(200),
]);
const figliOnboarding = z.union([
  z.string().regex(/^\d{0,3}$/),
  trimmedString(100),
]);

const longText = z
  .string()
  .trim()
  .max(2000, 'Massimo 2000 caratteri')
  .optional()
  .or(z.literal(''));

// ============================================================
// STEP 1 — Identita e contatti
// ============================================================
export const onboardingStep1Schema = z.object({
  nome: trimmedString(100).min(1, 'Nome obbligatorio'),
  cognome: trimmedString(100).min(1, 'Cognome obbligatorio'),
  email: trimmedString(255)
    .min(1, 'Email obbligatoria')
    .email('Formato email non valido'),
  telefono: trimmedString(20).refine(
    isValidPhoneIT,
    'Inserisci un numero italiano valido (es. 3331234567 o +39 333 1234567)'
  ),
  dataNascita: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato data: AAAA-MM-GG')
    .refine((s) => {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return false;
      const year = d.getFullYear();
      return year >= 1900 && year <= new Date().getFullYear();
    }, 'Data di nascita non valida'),
  sesso: z.enum(['M', 'F', 'altro'], { error: 'Seleziona il sesso' }),
  luogoNascita: trimmedString(100).min(1, 'Luogo di nascita obbligatorio'),
  provinciaNascita: trimmedString(2).optional(),
  nazioneNascita: trimmedString(100).default('Italia'),
  cf: trimmedString(16).refine(
    isValidCodiceFiscale,
    'Codice fiscale non valido (verifica le 16 lettere/numeri e il codice di controllo)'
  ),
  indirizzo: trimmedString(200).min(1, 'Indirizzo obbligatorio'),
  citta: trimmedString(100).min(1, 'Comune obbligatorio'),
  cap: trimmedString(5).regex(/^\d{5}$/, 'CAP non valido (5 cifre)'),
  provincia: trimmedString(2).regex(/^[A-Za-z]{2}$/, 'Provincia (2 lettere)'),
});
export type OnboardingStep1 = z.infer<typeof onboardingStep1Schema>;

// ============================================================
// STEP 2 — Storia clinica
// ============================================================
export const onboardingStep2Schema = z.object({
  storiaFamiliare: longText,
  patologie: longText,
  interventi: longText,
  farmaci: longText,
  allergieIntolleranze: longText,
});
export type OnboardingStep2 = z.infer<typeof onboardingStep2Schema>;

// ============================================================
// STEP 3 — Stile di vita
// ============================================================
export const onboardingStep3Schema = z.object({
  professione: trimmedString(200).optional().or(z.literal('')),
  statoCivile: statoCivileOnboarding.optional().or(z.literal('')),
  figli: figliOnboarding.optional().or(z.literal('')),
  obiettiviConsulenza: longText,
  doveMangia: trimmedString(200).optional().or(z.literal('')),
  alimentiSgraditi: longText,
  tipoDieta: tipoDietaOnboarding.optional().or(z.literal('')),
  alcol: trimmedString(100).optional().or(z.literal('')),
  momentoFame: trimmedString(200).optional().or(z.literal('')),
  mangiaVeloce: z.boolean().default(false),
  fameNervosa: z.boolean().default(false),
  sport: z.boolean().default(false),
  tipoSport: trimmedString(200).optional().or(z.literal('')),
  orariSport: trimmedString(200).optional().or(z.literal('')),
  altroNote: longText,
});
export type OnboardingStep3 = z.infer<typeof onboardingStep3Schema>;

// ============================================================
// STEP 4 — Consensi V1 (storico, usato dal gestionale fino a Fase 5)
// ============================================================
export const consensiSchema = z.object({
  art6_dati_personali: z
    .literal(true, { error: 'Consenso al trattamento obbligatorio.' }),
  art9_dati_sanitari: z
    .literal(true, { error: 'Consenso ai dati sanitari obbligatorio.' }),
  art7_marketing: z.boolean().default(false),
  dispositivo_wunder: z
    .literal(true, { error: 'Consenso bilancia Wunder obbligatorio.' }),
  dispositivo_genius_tsf: z
    .literal(true, { error: 'Consenso Genius TSF obbligatorio.' }),
});
export type Consensi = z.infer<typeof consensiSchema>;

// ============================================================
// PAYLOAD COMPLETO completeOnboarding (modello V2)
// ============================================================
export const completeOnboardingSchema = z.object({
  token: z
    .string()
    .regex(
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
      'Link non valido'
    ),
  step1: onboardingStep1Schema,
  step2: onboardingStep2Schema,
  step3: onboardingStep3Schema,
  documenti: z.array(documentoAccettatoSchema).min(1, 'Nessun documento inviato'),
});
export type CompleteOnboardingPayload = z.infer<typeof completeOnboardingSchema>;

// ============================================================
// Validate token + track progress (input ridotti)
// ============================================================
export const validateTokenSchema = z.object({
  token: z
    .string()
    .regex(
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    ),
});
export type ValidateTokenInput = z.infer<typeof validateTokenSchema>;

export const trackProgressSchema = z.object({
  token: validateTokenSchema.shape.token,
  step: z.number().int().min(1).max(4),
});
export type TrackProgressInput = z.infer<typeof trackProgressSchema>;

// ============================================================
// Staff: createPaziente + sendOnboardingLink + revokeOnboardingToken
// ============================================================
export const createPazienteSchema = z.object({
  cognome: trimmedString(100).min(1, 'Cognome obbligatorio'),
  nome: trimmedString(100).min(1, 'Nome obbligatorio'),
  email: trimmedString(255).email('Formato email non valido'),
  telefono: trimmedString(20).refine(
    isValidPhoneIT,
    'Numero non valido (mobile italiano richiesto)'
  ),
  /** Solo Titolare/Segreteria/Admin lo specificano. Dietologa: forzato auth.uid. */
  dietologaId: trimmedString(128).optional(),
  /** Se true, dopo createPaziente la CF chiama anche sendOnboardingLink. */
  sendLinkImmediately: z.boolean().default(false),
  /**
   * Nota in evidenza opzionale precompilata alla creazione. Passthrough:
   * se non vuota la CF crea una nota pinned riusando la validazione di
   * CreateNoteInput.text (lunghezza max applicata server-side, non qui).
   */
  notaText: z.string().trim().optional(),
});
export type CreatePazienteInput = z.infer<typeof createPazienteSchema>;

export const pazienteIdSchema = z.object({
  pazienteId: trimmedString(128).min(1),
});
export type PazienteIdInput = z.infer<typeof pazienteIdSchema>;
